// src/services/productionCostService.js
// Merkezi üretim maliyet ve cari hesap yönetim servisi

import { doc, updateDoc, addDoc, collection, getDoc, getDocs, query, where, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Tedarikçi bakiyesini günceller ve işlem kaydı oluşturur
 * @param {string} companyId - Şirket ID
 * @param {string} supplierId - Tedarikçi ID
 * @param {number} amount - Tutar (pozitif = borç artırır, negatif = borç azaltır)
 * @param {string} description - İşlem açıklaması
 * @param {object} metadata - Ek bilgiler (productionOrderId, stageId, etc.)
 * @returns {Promise<string>} Transaction ID
 */
export const updateSupplierBalance = async (companyId, supplierId, amount, description, metadata = {}) => {
  try {
    // 1. Tedarikçi bakiyesini güncelle
    const supplierRef = doc(db, 'companies', companyId, 'suppliers', supplierId);
    const supplierDoc = await getDoc(supplierRef);
    
    if (!supplierDoc.exists()) {
      throw new Error(`Tedarikçi bulunamadı: ${supplierId}`);
    }
    
    const supplierData = supplierDoc.data();
    const currentBalance = supplierData.balance || 0;
    const newBalance = currentBalance - amount; // Negatif = borçlu, pozitif = alacaklı
    
    await updateDoc(supplierRef, { 
      balance: newBalance,
      lastTransactionDate: new Date()
    });
    
    // 2. İşlem kaydı oluştur
    const transactionData = {
      type: metadata.transactionType || 'Üretim Maliyeti',
      amount: amount,
      description: description,
      date: new Date(),
      relatedPartyId: supplierId,
      relatedPartyName: supplierData.name,
      relatedPartyType: 'supplier',
      companyId: companyId,
      createdAt: serverTimestamp(),
      ...metadata
    };
    
    const transactionRef = await addDoc(
      collection(db, 'companies', companyId, 'transactions'),
      transactionData
    );
    
    console.log(`Tedarikçi bakiyesi güncellendi: ${supplierData.name} → ${newBalance} (${description})`);
    return transactionRef.id;
  } catch (error) {
    console.error('Tedarikçi bakiyesi güncellenirken hata:', error);
    throw error;
  }
};

/**
 * Fason tedarikçi bakiyesini günceller (subcontractors koleksiyonu)
 */
export const updateSubcontractorBalance = async (companyId, subcontractorId, amount, description, metadata = {}) => {
  try {
    const subRef = doc(db, 'companies', companyId, 'subcontractors', subcontractorId);
    const subDoc = await getDoc(subRef);
    
    if (!subDoc.exists()) {
      throw new Error(`Fason tedarikçi bulunamadı: ${subcontractorId}`);
    }
    
    const subData = subDoc.data();
    const currentBalance = subData.balance || 0;
    const newBalance = currentBalance - amount;
    
    await updateDoc(subRef, { 
      balance: newBalance,
      lastTransactionDate: new Date()
    });
    
    // İşlem kaydı
    await addDoc(collection(db, 'companies', companyId, 'transactions'), {
      type: metadata.transactionType || 'Fason İşçilik',
      amount: amount,
      description: description,
      date: new Date(),
      relatedPartyId: subcontractorId,
      relatedPartyName: subData.name,
      relatedPartyType: 'subcontractor',
      companyId: companyId,
      createdAt: serverTimestamp(),
      ...metadata
    });
    
    console.log(`Fason tedarikçi bakiyesi güncellendi: ${subData.name} → ${newBalance}`);
  } catch (error) {
    console.error('Fason tedarikçi bakiyesi güncellenirken hata:', error);
    throw error;
  }
};

/**
 * Stok hareketi kaydeder (ürün girişi veya çıkışı)
 */
export const recordStockMovement = async (companyId, productId, quantity, type, reference = {}) => {
  try {
    const productRef = doc(db, 'companies', companyId, 'products', productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      console.warn(`Ürün bulunamadı: ${productId}`);
      return false;
    }
    
    const productData = productDoc.data();
    const currentQuantity = productData.quantity || 0;
    const newQuantity = Math.max(0, currentQuantity + quantity); // Negatif stok olmaması için
    
    // Stok miktarını güncelle
    await updateDoc(productRef, { quantity: newQuantity });
    
    // Hareket kaydı oluştur
    const movementRef = doc(collection(db, 'companies', companyId, 'products', productId, 'movements'));
    await setDoc(movementRef, {
      date: new Date(),
      type: type,
      quantity: `${quantity > 0 ? '+' : ''}${quantity} ${productData.unit || 'Adet'}`,
      productName: productData.name,
      previousStock: currentQuantity,
      newStock: newQuantity,
      ...reference
    });
    
    console.log(`Stok hareketi: ${productData.name} ${quantity > 0 ? '+' : ''}${quantity} → Yeni stok: ${newQuantity}`);
    return true;
  } catch (error) {
    console.error('Stok hareketi kaydedilirken hata:', error);
    throw error;
  }
};

/**
 * Ürünü ismiyle bulur
 */
export const findProductByName = async (companyId, productName) => {
  try {
    const productsQuery = query(collection(db, 'companies', companyId, 'products'));
    const snapshot = await getDocs(productsQuery);
    
    // Exact match first
    let found = snapshot.docs.find(d => d.data().name === productName);
    
    // Case-insensitive match
    if (!found) {
      found = snapshot.docs.find(d => 
        d.data().name && d.data().name.toLowerCase() === productName.toLowerCase()
      );
    }
    
    if (found) {
      return { id: found.id, ...found.data() };
    }
    return null;
  } catch (error) {
    console.error('Ürün aranırken hata:', error);
    return null;
  }
};

/**
 * Malzeme satın alma siparişi oluşturur
 */
export const createMaterialPurchaseOrder = async (companyId, orderData) => {
  try {
    const purchaseOrderData = {
      productionOrderId: orderData.productionOrderId || '',
      supplierId: orderData.supplierId,
      supplierName: orderData.supplierName,
      items: orderData.items || [],
      status: 'Oluşturuldu', // Oluşturuldu → Sipariş Verildi → Yolda → Teslim Alındı
      totalCost: orderData.items.reduce((sum, item) => sum + ((item.unitCost || 0) * (item.quantity || 0)), 0),
      currency: orderData.currency || 'TRY',
      notes: orderData.notes || '',
      orderDate: new Date(),
      expectedDeliveryDate: orderData.expectedDeliveryDate || null,
      receivedDate: null,
      companyId: companyId,
      createdAt: serverTimestamp(),
      createdBy: orderData.createdBy || ''
    };
    
    const docRef = await addDoc(
      collection(db, 'companies', companyId, 'materialPurchaseOrders'),
      purchaseOrderData
    );
    
    console.log(`Malzeme siparişi oluşturuldu: ${docRef.id}`);
    return docRef.id;
  } catch (error) {
    console.error('Malzeme siparişi oluşturulurken hata:', error);
    throw error;
  }
};

/**
 * Malzeme siparişi teslim alındığında çağrılır
 * - Stok günceller
 * - Tedarikçi carisine yazar
 * - İşlem kaydı oluşturur
 */
export const receiveMaterialPurchaseOrder = async (companyId, purchaseOrderId, receivedItems) => {
  try {
    const orderRef = doc(db, 'companies', companyId, 'materialPurchaseOrders', purchaseOrderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      throw new Error(`Sipariş bulunamadı: ${purchaseOrderId}`);
    }
    
    const orderData = orderDoc.data();
    
    // 1. Her ürün için stok girişi yap
    for (const item of receivedItems) {
      if (item.productId && item.receivedQuantity > 0) {
        await recordStockMovement(
          companyId,
          item.productId,
          item.receivedQuantity,
          `Hammadde Alımı - ${orderData.supplierName}`,
          {
            purchaseOrderId: purchaseOrderId,
            productionOrderId: orderData.productionOrderId,
            supplierId: orderData.supplierId
          }
        );
      }
    }
    
    // 2. Toplam maliyeti hesapla
    const totalCost = receivedItems.reduce((sum, item) => 
      sum + ((item.unitCost || 0) * (item.receivedQuantity || 0)), 0
    );
    
    // 3. Tedarikçi carisine yaz
    if (orderData.supplierId && totalCost > 0) {
      await updateSupplierBalance(
        companyId,
        orderData.supplierId,
        totalCost,
        `Hammadde alımı - Sipariş #${purchaseOrderId.substring(0, 8)}`,
        {
          transactionType: 'Hammadde Alımı',
          purchaseOrderId: purchaseOrderId,
          productionOrderId: orderData.productionOrderId
        }
      );
    }
    
    // 4. Sipariş durumunu güncelle
    await updateDoc(orderRef, {
      status: 'Teslim Alındı',
      receivedDate: new Date(),
      receivedItems: receivedItems,
      actualTotalCost: totalCost,
      updatedAt: new Date()
    });
    
    console.log(`Sipariş teslim alındı: ${purchaseOrderId}, Toplam: ${totalCost}₺`);
    return { totalCost };
  } catch (error) {
    console.error('Sipariş teslim alınırken hata:', error);
    throw error;
  }
};

/**
 * Aksesuar siparişi oluşturur
 */
export const createAccessoryOrder = async (companyId, orderData) => {
  try {
    const accessoryOrderData = {
      productionOrderId: orderData.productionOrderId || '',
      productName: orderData.productName || '',
      supplierId: orderData.supplierId,
      supplierName: orderData.supplierName,
      items: orderData.items || [], // [{productId, productName, quantity, unit, measurement, unitCost}]
      status: 'Oluşturuldu',
      totalCost: orderData.items.reduce((sum, item) => sum + ((item.unitCost || 0) * (item.quantity || 0)), 0),
      currency: orderData.currency || 'TRY',
      notes: orderData.notes || '',
      orderDate: new Date(),
      expectedDeliveryDate: orderData.expectedDeliveryDate || null,
      receivedDate: null,
      companyId: companyId,
      createdAt: serverTimestamp(),
      createdBy: orderData.createdBy || ''
    };
    
    const docRef = await addDoc(
      collection(db, 'companies', companyId, 'accessoryOrders'),
      accessoryOrderData
    );
    
    console.log(`Aksesuar siparişi oluşturuldu: ${docRef.id}`);
    return docRef.id;
  } catch (error) {
    console.error('Aksesuar siparişi oluşturulurken hata:', error);
    throw error;
  }
};

/**
 * Aksesuar siparişi teslim alındığında çağrılır
 */
export const receiveAccessoryOrder = async (companyId, accessoryOrderId, receivedItems) => {
  try {
    const orderRef = doc(db, 'companies', companyId, 'accessoryOrders', accessoryOrderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      throw new Error(`Aksesuar siparişi bulunamadı: ${accessoryOrderId}`);
    }
    
    const orderData = orderDoc.data();
    
    // Stok girişi
    for (const item of receivedItems) {
      if (item.productId && item.receivedQuantity > 0) {
        await recordStockMovement(
          companyId,
          item.productId,
          item.receivedQuantity,
          `Aksesuar Alımı - ${orderData.supplierName}`,
          {
            accessoryOrderId: accessoryOrderId,
            productionOrderId: orderData.productionOrderId,
            supplierId: orderData.supplierId
          }
        );
      }
    }
    
    // Maliyet hesapla ve cariye yaz
    const totalCost = receivedItems.reduce((sum, item) => 
      sum + ((item.unitCost || 0) * (item.receivedQuantity || 0)), 0
    );
    
    if (orderData.supplierId && totalCost > 0) {
      await updateSupplierBalance(
        companyId,
        orderData.supplierId,
        totalCost,
        `Aksesuar alımı - ${orderData.productName || ''} - Sipariş #${accessoryOrderId.substring(0, 8)}`,
        {
          transactionType: 'Aksesuar Alımı',
          accessoryOrderId: accessoryOrderId,
          productionOrderId: orderData.productionOrderId
        }
      );
    }
    
    // Sipariş durumunu güncelle
    await updateDoc(orderRef, {
      status: 'Teslim Alındı',
      receivedDate: new Date(),
      receivedItems: receivedItems,
      actualTotalCost: totalCost,
      updatedAt: new Date()
    });
    
    return { totalCost };
  } catch (error) {
    console.error('Aksesuar siparişi teslim alınırken hata:', error);
    throw error;
  }
};

/**
 * Üretim aşaması tamamlandığında maliyet kaydeder ve cariye yazar
 * (Kesim, Dikim, Nakış vb. aşamalar için)
 */
export const recordStageCost = async (companyId, productionOrderId, stageData) => {
  try {
    const {
      stageId,
      stageName,
      stageType,
      supplierId,
      supplierName,
      totalCost,
      unitCost,
      quantity,
      isSubcontractor = false
    } = stageData;
    
    // Aşama maliyetini üretim emrine kaydet
    const orderRef = doc(db, 'companies', companyId, 'productionOrders', productionOrderId);
    const orderDoc = await getDoc(orderRef);
    
    if (orderDoc.exists()) {
      const orderData = orderDoc.data();
      const costBreakdown = orderData.costBreakdown || [];
      
      costBreakdown.push({
        stageId,
        stageName,
        stageType,
        supplierId,
        supplierName,
        totalCost,
        unitCost,
        quantity,
        recordedAt: new Date()
      });
      
      await updateDoc(orderRef, { 
        costBreakdown,
        updatedAt: new Date()
      });
    }
    
    // Tedarikçi carisine yaz
    if (supplierId && totalCost > 0) {
      const stageTypeLabels = {
        'cutting': 'Kesim',
        'sewing': 'Dikim',
        'embroidery': 'Nakış/Baskı',
        'accessory': 'Aksesuar Montajı',
        'subcontracting': 'Fason İşçilik',
        'internal': 'İç Üretim',
        'qualityControl': 'Kalite Kontrol',
        'packaging': 'Paketleme'
      };
      
      const label = stageTypeLabels[stageType] || stageName;
      const description = `${label} - ${stageName} (${quantity} adet × ${unitCost}₺)`;
      
      if (isSubcontractor) {
        await updateSubcontractorBalance(
          companyId, supplierId, totalCost, description,
          { transactionType: label, productionOrderId, stageId }
        );
      } else {
        await updateSupplierBalance(
          companyId, supplierId, totalCost, description,
          { transactionType: label, productionOrderId, stageId }
        );
      }
    }
    
    console.log(`Aşama maliyeti kaydedildi: ${stageName} → ${totalCost}₺`);
    return true;
  } catch (error) {
    console.error('Aşama maliyeti kaydedilirken hata:', error);
    throw error;
  }
};

/**
 * Toplam üretim maliyetini hesaplar
 */
export const calculateTotalProductionCost = async (companyId, productionOrderId) => {
  try {
    const orderRef = doc(db, 'companies', companyId, 'productionOrders', productionOrderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      throw new Error(`Üretim emri bulunamadı: ${productionOrderId}`);
    }
    
    const orderData = orderDoc.data();
    const costBreakdown = orderData.costBreakdown || [];
    
    const summary = {
      rawMaterials: 0,
      cutting: 0,
      accessories: 0,
      sewing: 0,
      embroidery: 0,
      subcontracting: 0,
      overhead: 0,
      total: 0,
      breakdown: costBreakdown
    };
    
    costBreakdown.forEach(cost => {
      const amount = cost.totalCost || 0;
      switch (cost.stageType) {
        case 'cutting':
          summary.cutting += amount;
          break;
        case 'accessory':
          summary.accessories += amount;
          break;
        case 'sewing':
          summary.sewing += amount;
          break;
        case 'embroidery':
          summary.embroidery += amount;
          break;
        case 'subcontracting':
          summary.subcontracting += amount;
          break;
        default:
          summary.rawMaterials += amount;
      }
    });
    
    // Malzeme siparişlerinden hammadde maliyetini ekle
    const materialOrdersQuery = query(
      collection(db, 'companies', companyId, 'materialPurchaseOrders'),
      where('productionOrderId', '==', productionOrderId)
    );
    const materialOrdersSnapshot = await getDocs(materialOrdersQuery);
    materialOrdersSnapshot.forEach(d => {
      const data = d.data();
      if (data.status === 'Teslim Alındı') {
        summary.rawMaterials += data.actualTotalCost || data.totalCost || 0;
      }
    });
    
    // Aksesuar siparişlerinden aksesuar maliyetini ekle
    const accessoryOrdersQuery = query(
      collection(db, 'companies', companyId, 'accessoryOrders'),
      where('productionOrderId', '==', productionOrderId)
    );
    const accessoryOrdersSnapshot = await getDocs(accessoryOrdersQuery);
    accessoryOrdersSnapshot.forEach(d => {
      const data = d.data();
      if (data.status === 'Teslim Alındı') {
        summary.accessories += data.actualTotalCost || data.totalCost || 0;
      }
    });
    
    // Genel gider (%10)
    const subtotal = summary.rawMaterials + summary.cutting + summary.accessories + 
                     summary.sewing + summary.embroidery + summary.subcontracting;
    summary.overhead = subtotal * 0.10;
    summary.total = subtotal + summary.overhead;
    
    return summary;
  } catch (error) {
    console.error('Toplam maliyet hesaplanırken hata:', error);
    throw error;
  }
};

export default {
  updateSupplierBalance,
  updateSubcontractorBalance,
  recordStockMovement,
  findProductByName,
  createMaterialPurchaseOrder,
  receiveMaterialPurchaseOrder,
  createAccessoryOrder,
  receiveAccessoryOrder,
  recordStageCost,
  calculateTotalProductionCost
};
